/**
 * Squish Deli shared UI helpers (FOUNDATION role). Loaded on every page from snippets/sd-head.liquid.
 *
 * Import in other modules:   import { addItems, deliveryEstimate } from '@theme/sd-ui';
 * Or from inline scripts:    window.SquishDeli.addItems([...])   (available after this module runs;
 *                            listen for the 'sd:ready' event on document if you run earlier)
 *
 * Cart adds go through /cart/add.js with Horizon's section rendering and dispatch the standard
 * CartLinesUpdateEvent from @shopify/events, exactly like assets/product-form.js. That keeps the
 * Horizon cart drawer, cart bubble, live region and cart page in sync with no extra code.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/* ------------------------------------------------------------------ */
/* Money                                                                */
/* ------------------------------------------------------------------ */

/**
 * Format cents as money. Whole amounts drop the decimals ($18), others keep two ($13).
 * @param {number} cents
 * @param {{currency?: string, keepZeros?: boolean}} [opts]
 * @returns {string}
 */
export function formatMoney(cents, opts = {}) {
  const currency = opts.currency || window.Shopify?.currency?.active || 'USD';
  const amount = Number(cents || 0) / 100;
  const whole = Math.round(amount * 100) % 100 === 0;
  const digits = whole && !opts.keepZeros ? 0 : 2;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(digits)}`;
  }
}

/* ------------------------------------------------------------------ */
/* Cart                                                                 */
/* ------------------------------------------------------------------ */

const routes = () => window.Theme?.routes || {};
const cartAddUrl = () => routes().cart_add_url || '/cart/add.js';
const cartUrl = () => routes().cart_url || '/cart';

/** @returns {Promise<any>} the AJAX cart (/cart.js) */
export async function getCart() {
  const itemsComponent = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (itemsComponent?.fetchCartData) {
    await customElements.whenDefined('cart-items-component');
    return itemsComponent.fetchCartData();
  }
  const res = await fetch(`${cartUrl()}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

/** Section ids of every rendered cart-items-component (drawer and/or cart page). */
function cartSectionIds() {
  const ids = [];
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  }
  return [...new Set(ids)];
}

/** Opens Horizon's cart drawer (falls back to the cart page). */
export function openCartDrawer() {
  const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
  if (drawer?.open) {
    if (!drawer.isOpen) drawer.open();
    return true;
  }
  if (window.Shopify?.actions?.openCart) {
    window.Shopify.actions.openCart();
    return true;
  }
  window.location.href = cartUrl();
  return false;
}

/**
 * Add one or more lines to the cart the Horizon way.
 *
 * @param {Array<{id: number|string, quantity?: number, properties?: Record<string, string>, selling_plan?: number|string}>} items
 * @param {{open?: boolean, source?: EventTarget, signal?: AbortSignal}} [options]
 *   open:   open the cart drawer when done (default true)
 *   source: element the event bubbles from (default document)
 * @returns {Promise<{cart: any, items: any[], sections: Record<string,string>}>}
 *   Rejects with an Error whose message is Shopify's customer-facing message (e.g. sold out).
 */
export async function addItems(items, options = {}) {
  const { open = true, source = document, signal } = options;
  const lines = (items || [])
    .filter((item) => item && item.id)
    .map((item) => ({
      id: Number(String(item.id).split('/').pop()),
      quantity: Math.max(1, Number(item.quantity) || 1),
      ...(item.properties && Object.keys(item.properties).length ? { properties: item.properties } : {}),
      ...(item.selling_plan ? { selling_plan: item.selling_plan } : {}),
    }));
  if (!lines.length) throw new Error('Nothing to add');

  const sections = cartSectionIds();
  const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  let response;
  try {
    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, ...(sections.length ? { sections: sections.join(',') } : {}) }),
      signal,
    });
    response = await res.json();
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    throw new Error(window.Theme?.translations?.cart_error || 'Something went wrong. Please try again.');
  }

  if (response?.status) {
    const message = response.description || response.message || 'Could not add that to your bag.';
    source.dispatchEvent(
      new CartErrorEvent({
        error: response.message || 'Add to cart failed',
        code: 'INVALID',
        detail: { description: response.description, errors: response.errors },
      })
    );
    getCart()
      .then((cart) =>
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'sd-ui', itemCount: totalQuantity },
        })
      )
      .catch(deferred.reject);
    throw new Error(message);
  }

  const cart = await getCart();
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source: 'sd-ui',
      itemCount: totalQuantity,
      sections: response.sections,
      didError: false,
    },
  });

  const onCartPage = (document.getElementById('MainContent')?.dataset.template || '').startsWith('cart');
  if (open && !onCartPage) {
    // Let the drawer morph its new contents first, then slide it in.
    requestAnimationFrame(() => openCartDrawer());
  }
  return { cart, items: response.items || [], sections: response.sections || {} };
}

/* ------------------------------------------------------------------ */
/* Dates: America/New_York, business days, holiday phase                */
/* ------------------------------------------------------------------ */

const TZ = 'America/New_York';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** QA override: ?sd_date=2026-11-24 pretends today is that New York date (JS only). */
function overrideDate() {
  try {
    const value = new URL(window.location.href).searchParams.get('sd_date');
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Today's calendar date in New York as a UTC-midnight Date (safe for day arithmetic).
 * @param {Date} [now]
 */
export function nyToday(now = new Date()) {
  const forced = overrideDate();
  if (forced) return new Date(`${forced}T00:00:00Z`);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  return new Date(`${parts}T00:00:00Z`);
}

const ymd = (d) => d.toISOString().slice(0, 10);
const utc = (y, m, d) => new Date(Date.UTC(y, m, d));
/** nth weekday (0=Sun) of month m (0-based); n = -1 for last */
function nthWeekday(y, m, weekday, n) {
  if (n > 0) {
    const first = utc(y, m, 1);
    const offset = (weekday - first.getUTCDay() + 7) % 7;
    return utc(y, m, 1 + offset + (n - 1) * 7);
  }
  const last = utc(y, m + 1, 0);
  const offset = (last.getUTCDay() - weekday + 7) % 7;
  return utc(y, m, last.getUTCDate() - offset);
}

/** Observed date for fixed holidays that land on a weekend. */
function observed(d) {
  const day = d.getUTCDay();
  if (day === 6) return utc(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1);
  if (day === 0) return utc(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
  return d;
}

const holidayCache = new Map();
/** US federal holidays (observed) for a year, as a Set of YYYY-MM-DD. */
export function usHolidays(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const list = [
    observed(utc(year, 0, 1)), // New Year's Day
    nthWeekday(year, 0, 1, 3), // MLK Day
    nthWeekday(year, 1, 1, 3), // Presidents Day
    nthWeekday(year, 4, 1, -1), // Memorial Day
    observed(utc(year, 5, 19)), // Juneteenth
    observed(utc(year, 6, 4)), // Independence Day
    nthWeekday(year, 8, 1, 1), // Labor Day
    nthWeekday(year, 9, 1, 2), // Columbus / Indigenous Peoples' Day
    observed(utc(year, 10, 11)), // Veterans Day
    nthWeekday(year, 10, 4, 4), // Thanksgiving
    observed(utc(year, 11, 25)), // Christmas
  ];
  const set = new Set(list.map(ymd));
  holidayCache.set(year, set);
  return set;
}

export function isBusinessDay(d) {
  const day = d.getUTCDay();
  if (day === 0 || day === 6) return false;
  return !usHolidays(d.getUTCFullYear()).has(ymd(d));
}

/** Add n business days to a UTC-midnight date (counting starts the day after `from`). */
export function addBusinessDays(from, n) {
  const d = new Date(from.getTime());
  let left = n;
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (isBusinessDay(d)) left -= 1;
  }
  return d;
}

/**
 * Format a UTC-midnight date: "Nov 13" or with weekday "Fri, Nov 13".
 * @param {Date} d
 * @param {{weekday?: boolean}} [opts]
 */
export function formatShortDate(d, opts = {}) {
  const base = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return opts.weekday ? `${DAYS[d.getUTCDay()]}, ${base}` : base;
}

/**
 * Delivery estimate: today (New York) + 8 to +18 business days, skipping weekends and US
 * federal holidays. Matches the bible examples (Mon Nov 2 -> Nov 13 - Nov 30).
 * @param {{from?: Date, min?: number, max?: number, weekday?: boolean}} [opts]
 * @returns {{start: Date, end: Date, startLabel: string, endLabel: string, label: string, text: string}}
 */
export function deliveryEstimate(opts = {}) {
  const from = opts.from || nyToday();
  const start = addBusinessDays(from, opts.min ?? 8);
  const end = addBusinessDays(from, opts.max ?? 18);
  const startLabel = formatShortDate(start, opts);
  const endLabel = formatShortDate(end, opts);
  const label = `${startLabel} – ${endLabel}`;
  return { start, end, startLabel, endLabel, label, text: `Est. arrival ${label}` };
}

export const HOLIDAY_DATES = Object.freeze({
  orderBy: '2026-11-22', // "Christmas: order by Sun Nov 22 · estimated arrival by Dec 17" (the ONE Christmas date)
  maybeStart: '2026-11-23', // Nov 23-24: "Ordering now may not arrive before Dec 25"
  lateStart: '2026-11-25', // Nov 25-Dec 25: "will most likely arrive after Christmas" + free printable reveal card
  offerEnd: '2026-10-19', // Opening offer (free Christmas Mini) ended early: the Mini was dropped (2026-10-20)
  christmas: '2026-12-25',
  afterStart: '2026-12-26', // normal lines again
});

/**
 * Holiday phase for a New York date (BUILD-SPEC rev 2 section 5; there is no Nov 30 final call and no Feast offer).
 *   'pre'    until Sun Nov 22 23:59 ET
 *   'maybe'  Mon Nov 23 - Tue Nov 24
 *   'late'   Wed Nov 25 - Fri Dec 25
 *   'after'  from Sat Dec 26
 * `offer` is true while the opening offer runs (through Mon Nov 30).
 * @param {Date} [today] UTC-midnight date (defaults to nyToday())
 * @returns {{phase: 'pre'|'maybe'|'late'|'after', offer: boolean, today: string}}
 */
export function holidayPhase(today = nyToday()) {
  const t = ymd(today);
  let phase = 'pre';
  if (t >= HOLIDAY_DATES.afterStart) phase = 'after';
  else if (t >= HOLIDAY_DATES.lateStart) phase = 'late';
  else if (t >= HOLIDAY_DATES.maybeStart) phase = 'maybe';
  return {
    phase: /** @type {any} */ (phase),
    offer: t <= HOLIDAY_DATES.offerEnd,
    today: t,
  };
}

/* ------------------------------------------------------------------ */
/* Declarative hooks                                                    */
/* ------------------------------------------------------------------ */

/**
 * Fill every [data-sd-delivery] with the estimate. data-sd-delivery="range" (default) | "start" |
 * "end" | "text"; add data-sd-weekday to include weekdays.
 */
export function renderDelivery(root = document) {
  for (const el of root.querySelectorAll('[data-sd-delivery]')) {
    const est = deliveryEstimate({ weekday: el.hasAttribute('data-sd-weekday') });
    const mode = el.getAttribute('data-sd-delivery') || 'range';
    el.textContent = mode === 'start' ? est.startLabel : mode === 'end' ? est.endLabel : mode === 'text' ? est.text : est.label;
  }
}

/**
 * Show/hide date-gated blocks. Liquid should gate the initial render too; this corrects cached pages and
 * powers ?sd_date QA.
 *   [data-sd-phase="pre maybe"]  shown only in the listed phases (space-separated)
 *   [data-sd-offer]              shown only while the opening offer runs (until Mon Nov 30); combines with data-sd-phase
 *   [data-sd-offer="off"]        shown only after the opening offer ended
 */
export function applyPhase(root = document) {
  const { phase, offer } = holidayPhase();
  document.documentElement.dataset.sdPhase = phase;
  document.documentElement.dataset.sdOffer = offer ? 'on' : 'off';
  for (const el of root.querySelectorAll('[data-sd-phase], [data-sd-offer]')) {
    let show = true;
    if (el.hasAttribute('data-sd-phase')) {
      const wanted = (el.getAttribute('data-sd-phase') || '').split(/\s+/).filter(Boolean);
      show = wanted.includes(phase);
    }
    if (el.hasAttribute('data-sd-offer')) {
      const want = el.getAttribute('data-sd-offer') === 'off' ? false : true;
      if (offer !== want) show = false;
    }
    el.hidden = !show;
  }
}

/* Reveal on scroll: [data-sd-reveal] fades + rises 16px once. */
let revealObserver;
export function initReveal(root = document) {
  const targets = root.querySelectorAll('[data-sd-reveal]:not(.is-revealed)');
  if (!targets.length) return;
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  targets.forEach((el) => revealObserver.observe(el));
}

/* Squish press: CSS :active handles pointers; this adds the same feel for keyboard presses and
   lets scripts trigger it (e.g. after a successful add). */
export function squish(el) {
  if (!(el instanceof HTMLElement) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  el.classList.remove('sd-squishing');
  void el.offsetWidth; // restart the animation
  el.classList.add('sd-squishing');
  el.addEventListener('animationend', () => el.classList.remove('sd-squishing'), { once: true });
}

/* "Order up" ticket toast (role=status). Use when you add without opening the drawer. */
let toastTimer;
export function orderUp(message, { title = 'Order up', duration = 3200 } = {}) {
  let toast = document.getElementById('sd-order-up');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'sd-order-up';
    toast.className = 'sd-order-up';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = '<span class="sd-order-up__title"></span><span class="sd-order-up__msg"></span>';
    document.body.appendChild(toast);
  }
  toast.querySelector('.sd-order-up__title').textContent = title;
  toast.querySelector('.sd-order-up__msg').textContent = message;
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), duration);
}

/* "Now serving" flip on the header cart count whenever lines are added. */
function wireCartBubble() {
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        for (const bubble of document.querySelectorAll('.cart-bubble')) {
          bubble.classList.remove('sd-now-serving');
          void bubble.offsetWidth;
          bubble.classList.add('sd-now-serving');
        }
      })
      .catch(() => {});
  });
}

/**
 * Quick add buttons: <button data-sd-add data-variant-id="123" [data-quantity="1"]
 *   [data-properties='{"_plate":"party"}'] [data-sd-no-drawer]>. Optional sibling/child
 *   [data-sd-add-status] receives errors. Used by snippets/sd-product-card.liquid.
 */
function wireQuickAdd() {
  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-sd-add]') : null;
    if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    // The variant picker belongs to the button's own card or form only (never another card in the section).
    const pickerScope = button.closest('[data-sd-card], form');
    const picker = pickerScope?.querySelector('[data-sd-variant-select]');
    const scopeEl = button.closest('[data-sd-card], form, section') || button.parentElement;
    const id = picker instanceof HTMLSelectElement && picker.value ? picker.value : button.dataset.variantId;
    if (!id) return;
    let properties;
    try {
      properties = button.dataset.properties ? JSON.parse(button.dataset.properties) : undefined;
    } catch {
      properties = undefined;
    }
    const status = scopeEl?.querySelector('[data-sd-add-status]');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    if (status) status.textContent = '';
    try {
      await addItems([{ id, quantity: Number(button.dataset.quantity) || 1, properties }], {
        source: button,
        open: !button.hasAttribute('data-sd-no-drawer'),
      });
      markAdded(button);
      if (button.hasAttribute('data-sd-no-drawer')) orderUp(`${button.dataset.productTitle || 'Squishy'} is on your plate.`);
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : String(error);
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  });

  // Keyboard presses get the squish too (pointer presses use CSS :active).
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const el = event.target instanceof Element ? event.target.closest('.sd-btn, .button') : null;
    if (el instanceof HTMLElement) squish(el);
  });
}

/* ------------------------------------------------------------------ */
/* Style sheet: multi-style cards pick a style before adding            */
/* ------------------------------------------------------------------ */

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

/** Shopify CDN image URL at another width (keeps the other params). */
function imageAt(url, width) {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('width', String(width));
    return url.startsWith('//') ? u.toString().replace(/^https?:/, '') : u.toString();
  } catch {
    return url;
  }
}

const CLOSE_ICON =
  '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M200 56 56 200M200 200 56 56" fill="none" stroke="currentColor" stroke-width="24" stroke-linecap="round"/></svg>';

let styleSheetEl = null;
/** @type {{data: any, trigger: HTMLElement, card: Element | null} | null} */
let styleCtx = null;

function ensureStyleSheet() {
  if (styleSheetEl) return styleSheetEl;
  const dialog = document.createElement('dialog');
  dialog.className = 'sd-sheet';
  dialog.id = 'sd-style-sheet';
  dialog.setAttribute('aria-labelledby', 'sd-sheet-title');
  dialog.innerHTML = `
    <div class="sd-sheet__inner">
      <div class="sd-sheet__grab" aria-hidden="true"></div>
      <div class="sd-sheet__head">
        <img class="sd-sheet__img" alt="" width="120" height="120" data-sd-sheet-img>
        <div>
          <p class="sd-sheet__eyebrow" data-sd-sheet-eyebrow></p>
          <h2 class="sd-sheet__title" id="sd-sheet-title" data-sd-sheet-title></h2>
          <p class="sd-sheet__price" data-sd-sheet-price></p>
        </div>
        <button type="button" class="sd-sheet__close" data-sd-sheet-close aria-label="Close">${CLOSE_ICON}</button>
      </div>
      <fieldset class="sd-sheet__styles">
        <legend class="sd-sheet__legend" data-sd-sheet-legend></legend>
        <div class="sd-sheet__chips" data-sd-sheet-chips></div>
      </fieldset>
      <p class="sd-sheet__note">You get one piece, in the style you pick. Not food.</p>
      <div class="sd-sheet__actions">
        <button type="button" class="sd-btn sd-btn--primary" data-sd-sheet-add><span data-sd-sheet-add-label>Add to plate</span></button>
        <a class="sd-btn-text" href="#" data-sd-sheet-link>See details</a>
        <p class="sd-sheet__status" role="status" aria-live="polite" data-sd-sheet-status></p>
      </div>
    </div>`;
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (event) => {
    const t = event.target;
    if (t === dialog) return dialog.close();
    if (!(t instanceof Element)) return;
    if (t.closest('[data-sd-sheet-close]')) return dialog.close();
    if (t.closest('[data-sd-sheet-add]')) addFromStyleSheet();
  });
  dialog.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.name === 'sd-style') showStyle(input.value);
  });
  dialog.addEventListener('close', () => {
    const ctx = styleCtx;
    styleCtx = null;
    document.documentElement.classList.remove('sd-sheet-open');
    // Hand focus back to the card button unless the cart drawer took over.
    if (ctx?.trigger && !document.querySelector('theme-drawer#cart-drawer[open], #cart-drawer dialog[open]')) {
      ctx.trigger.focus({ preventScroll: true });
    }
  });
  styleSheetEl = dialog;
  return dialog;
}

function showStyle(id) {
  if (!styleSheetEl || !styleCtx) return;
  const style = styleCtx.data.styles.find((s) => String(s.id) === String(id));
  const img = styleSheetEl.querySelector('[data-sd-sheet-img]');
  if (style && img instanceof HTMLImageElement && style.img) {
    img.src = imageAt(style.img, 240);
    img.alt = `${styleCtx.data.title}, ${style.name}: the style you get (one piece)`;
  }
}

/**
 * Open the style sheet for a card button. The card must contain <script type="application/json" data-sd-styles>.
 * @param {HTMLElement} trigger
 * @returns {boolean} false when there is no style data (caller falls back to a direct add)
 */
export function openStyleSheet(trigger) {
  const card = trigger.closest('[data-sd-card]');
  const json = card?.querySelector('script[data-sd-styles]');
  let data;
  try {
    data = JSON.parse(json?.textContent || '');
  } catch {
    return false;
  }
  if (!data?.styles?.length) return false;
  const dialog = ensureStyleSheet();
  styleCtx = { data, trigger, card };

  const money = (cents) => formatMoney(cents);
  dialog.querySelector('[data-sd-sheet-eyebrow]').textContent = data.counter || '';
  dialog.querySelector('[data-sd-sheet-title]').textContent = data.title;
  const price = dialog.querySelector('[data-sd-sheet-price]');
  if (data.plate) {
    price.innerHTML = `<span class="sd-price-tag sd-price-tag--stack"><span class="sd-price-tag__amt">${escapeHtml(
      money(data.plate)
    )}</span><span class="sd-price-tag__note">each in a<br>Party Platter</span></span><span class="sd-sheet__single">${escapeHtml(money(data.price))} on its own</span>`;
  } else if (data.addon) {
    price.innerHTML = `<span class="sd-price-tag">${escapeHtml(money(data.price))}</span><span>add-on</span>`;
  } else {
    price.innerHTML = `<span class="sd-price-tag">${escapeHtml(money(data.price))}</span>`;
  }
  const available = data.styles.filter((s) => s.ok).length;
  dialog.querySelector('[data-sd-sheet-legend]').innerHTML = `Pick a ${escapeHtml(String(data.option || 'Style').toLowerCase())} <span>${available} to choose from</span>`;

  const selected = data.styles.find((s) => s.ok && String(s.id) === String(data.selected)) || data.styles.find((s) => s.ok);
  dialog.querySelector('[data-sd-sheet-chips]').innerHTML = data.styles
    .map((s) => {
      const on = selected && s.id === selected.id;
      return `<label class="sd-style"><input type="radio" name="sd-style" value="${escapeHtml(s.id)}"${on ? ' checked' : ''}${
        s.ok ? '' : ' disabled'
      }>${s.img ? `<img src="${escapeHtml(imageAt(s.img, 120))}" alt="" width="42" height="42" loading="lazy">` : ''}<span class="sd-style__name">${escapeHtml(
        s.name
      )}${s.ok ? '' : '<span class="sd-style__out">Sold out</span>'}</span></label>`;
    })
    .join('');

  const label = dialog.querySelector('[data-sd-sheet-add-label]');
  label.textContent = data.label || 'Add to plate';
  const link = dialog.querySelector('[data-sd-sheet-link]');
  link.setAttribute('href', data.url || '#');
  dialog.querySelector('[data-sd-sheet-status]').textContent = '';
  if (selected) showStyle(selected.id);

  document.documentElement.classList.add('sd-sheet-open');
  if (!dialog.open) dialog.showModal();
  // Focus the chosen style so arrow keys move between styles straight away.
  dialog.querySelector('input[name="sd-style"]:checked')?.focus({ preventScroll: true });
  dialog.querySelector('.sd-sheet__inner').scrollTop = 0;
  return true;
}

async function addFromStyleSheet() {
  const dialog = styleSheetEl;
  const ctx = styleCtx;
  if (!dialog || !ctx) return;
  const button = dialog.querySelector('[data-sd-sheet-add]');
  if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;
  const picked = dialog.querySelector('input[name="sd-style"]:checked');
  const status = dialog.querySelector('[data-sd-sheet-status]');
  if (!(picked instanceof HTMLInputElement)) {
    status.textContent = 'Pick a style first.';
    return;
  }
  const noDrawer = ctx.trigger.hasAttribute('data-sd-no-drawer');
  button.setAttribute('aria-busy', 'true');
  button.classList.add('is-loading');
  status.textContent = '';
  try {
    await addItems([{ id: picked.value, quantity: Number(ctx.trigger.dataset.quantity) || 1 }], { source: ctx.trigger, open: false });
    const style = ctx.data.styles.find((s) => String(s.id) === picked.value);
    dialog.close();
    markAdded(ctx.trigger);
    const onCartPage = (document.getElementById('MainContent')?.dataset.template || '').startsWith('cart');
    if (noDrawer || onCartPage) orderUp(`${ctx.data.title}${style ? `, ${style.name}` : ''} is on your plate.`);
    else requestAnimationFrame(() => openCartDrawer());
  } catch (error) {
    status.textContent = error instanceof Error ? error.message : String(error);
  } finally {
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
  }
}

/** Card button feedback after a successful add. */
function markAdded(button) {
  const label = button.querySelector('[data-sd-add-label]') || button;
  const original = button.dataset.sdLabel || label.textContent;
  button.dataset.sdLabel = original;
  squish(button);
  label.textContent = button.dataset.addedLabel || 'On your plate';
  button.classList.add('is-added');
  clearTimeout(Number(button.dataset.sdTimer));
  button.dataset.sdTimer = String(
    setTimeout(() => {
      label.textContent = original;
      button.classList.remove('is-added');
    }, 1800)
  );
}

function wireStyleSheet() {
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-sd-choose]') : null;
    if (!(trigger instanceof HTMLElement) || trigger.hasAttribute('disabled')) return;
    event.preventDefault();
    if (openStyleSheet(trigger)) return;
    // No style data: fall back to adding the default style.
    trigger.setAttribute('data-sd-add', '');
    trigger.removeAttribute('data-sd-choose');
    trigger.click();
  });
}

/* Touch press for slow-rise cards: hold = squash, release = slow rise (CSS does the timing). */
function wirePressables() {
  const release = (event) => {
    const el = event.target instanceof Element ? event.target.closest('[data-sd-press]') : null;
    el?.classList.remove('is-pressed');
  };
  document.addEventListener(
    'pointerdown',
    (event) => {
      const el = event.target instanceof Element ? event.target.closest('[data-sd-press]') : null;
      if (el) el.classList.add('is-pressed');
    },
    { passive: true }
  );
  ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) =>
    document.addEventListener(type, release, { passive: true, capture: type === 'pointerleave' })
  );
}

function refresh(root = document) {
  renderDelivery(root);
  applyPhase(root);
  initReveal(root);
}

const api = {
  addItems,
  getCart,
  openCartDrawer,
  formatMoney,
  nyToday,
  usHolidays,
  isBusinessDay,
  addBusinessDays,
  formatShortDate,
  deliveryEstimate,
  holidayPhase,
  HOLIDAY_DATES,
  renderDelivery,
  applyPhase,
  initReveal,
  squish,
  orderUp,
  openStyleSheet,
  refresh,
};

if (!window.SquishDeli) {
  window.SquishDeli = api;
  wireQuickAdd();
  wireStyleSheet();
  wirePressables();
  wireCartBubble();
  const boot = () => {
    refresh();
    document.dispatchEvent(new CustomEvent('sd:ready', { detail: api }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  // Theme editor re-renders
  document.addEventListener('shopify:section:load', (event) => refresh(/** @type {any} */ (event.target)));
}

export default api;
